import {
  createResource,
  deleteResource,
  determinePaginationParams,
  determineSelector,
  getResource,
  readUrl,
  updateResource,
  updateResourcePath,
} from "./api";

const get = async (id) => {
  const selector = determineSelector(id, { keyId: "profile_id", keySlug: "slug" });
  const url = readUrl("user_profiles", { selector });
  return getResource(url);
};

const getByTeamId = async (teamId, options = { page: 1, per_page: 50 }) => {
  const selector = `team_id=eq.${teamId}`;
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("user_profiles", { selector, paginationParams });
  return getResource(url);
};

const getByTeamIds = async (teamIds, options = { page: 1, per_page: 50 }) => {
  const selector = `team_id=in.(${teamIds.join(",")})`;
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("user_profiles", { selector, paginationParams });
  return getResource(url);
};

// Server-side ilike on name + exclusion list. Empty query returns top `limit` by name.
const searchByTeamIds = async (teamIds, { query = "", excludeSlugs = [], limit = 25 } = {}) => {
  const filters = [`team_id=in.(${teamIds.join(",")})`];

  const trimmed = query.trim();
  if (trimmed) {
    filters.push(`name=ilike.*${encodeURIComponent(trimmed)}*`);
  }

  if (excludeSlugs.length > 0) {
    filters.push(`slug=not.in.(${excludeSlugs.join(",")})`);
  }

  const selector = `${filters.join("&")}&order=name.asc&limit=${limit}`;
  const url = readUrl("user_profiles", { selector });
  return getResource(url);
};

const getAllBySlugs = async (slugs, options = { page: 1, per_page: 50 }) => {
  const selector = `slug=in.(${slugs.join(",")})`;
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("user_profiles", { selector, paginationParams });
  return getResource(url);
};

// Resolve a V1 user (by their numeric V1 user id) to their V2 profile. Used by the V1-hosted
// peer-DM compose modal to map the V1 user a card carries onto the V2 profile_id + granular
// type_of_profile. Goes through the same PostgREST/token-refresh layer as the other reads.
const getByUserMigrateId = async (userMigrateId) => {
  const selector = `user_migrate_id=eq.${userMigrateId}`;
  const url = readUrl("user_profiles", { selector });
  return getResource(url);
};

// Batch-resolve V2 profile_ids to their authors' V1 PrUser ids (`migrate_id`), for V1-hosted
// `/pr_users/:id` bylines. Returns { data: [{ profile_id, migrate_id }], error }.
const getMigrateIdsByProfileIds = async (profileIds) => {
  if (!profileIds || profileIds.length === 0) return { data: [] };
  const selector = `select=profile_id,migrate_id&profile_id=in.(${profileIds.join(",")})`;
  const url = readUrl("user_profiles", { selector });
  return getResource(url);
};

// The profile-image trio mirrors experts.js/products.js/companies.js exactly — same nested
// `profile_image` route, same `{ data } | { error }` result — because <ProfileImage> routes through
// whichever of them owns the entity it was handed and can only do that if they answer alike. The
// owner here is the Id::Profile (media profile or PR profile), not a promo entity.
const profileImagePath = (userProfileId) => `${updateResourcePath("user_profiles", userProfileId)}/profile_image`;

const createProfileImageById = async (userProfileId, attributes) => {
  return createResource(profileImagePath(userProfileId), { data: { attributes } });
};

const updateProfileImageById = async (userProfileId, attributes) => {
  return updateResource(profileImagePath(userProfileId), { data: { attributes } });
};

const deleteProfileImageById = async (userProfileId) => {
  return deleteResource(profileImagePath(userProfileId));
};

export default {
  createProfileImageById,
  deleteProfileImageById,
  get,
  getAllBySlugs,
  getByTeamId,
  getByTeamIds,
  getByUserMigrateId,
  getMigrateIdsByProfileIds,
  searchByTeamIds,
  updateProfileImageById,
};
